import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2 } from 'lucide-react';

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { api, isAuthenticated } = useAuth();
  const [status, setStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }

    const checkStatus = async () => {
      try {
        const response = await api.get(`/payments/status/${sessionId}`);
        if (response.data.payment_status === 'paid') {
          setStatus('success');
        } else if (attempts < 5) {
          setTimeout(() => {
            setAttempts((prev) => prev + 1);
          }, 2000);
        } else {
          setStatus('pending');
        }
      } catch (error) {
        console.error('Failed to check payment status:', error);
        if (attempts < 5) {
          setTimeout(() => {
            setAttempts((prev) => prev + 1);
          }, 2000);
        } else {
          setStatus('error');
        }
      }
    };

    if (isAuthenticated) {
      checkStatus();
    }
  }, [sessionId, attempts, isAuthenticated, api]);

  return (
    <Layout showFooter={false}>
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center px-4 bg-secondary" data-testid="payment-success-page">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            {status === 'checking' ? (
              <>
                <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
                <h1 className="font-serif text-2xl font-bold mb-2">Processing Payment</h1>
                <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
              </>
            ) : status === 'success' ? (
              <>
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h1 className="font-serif text-2xl font-bold mb-2">Payment Successful!</h1>
                <p className="text-muted-foreground mb-6">
                  Your booking has been confirmed. You can now message the host.
                </p>
                <div className="space-y-2">
                  <Button asChild className="w-full rounded-full">
                    <Link to="/guest/dashboard">View My Bookings</Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full rounded-full">
                    <Link to="/weddings">Browse More Weddings</Link>
                  </Button>
                </div>
              </>
            ) : status === 'pending' ? (
              <>
                <Loader2 className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                <h1 className="font-serif text-2xl font-bold mb-2">Payment Processing</h1>
                <p className="text-muted-foreground mb-6">
                  Your payment is being processed. This may take a few minutes.
                </p>
                <Button asChild className="w-full rounded-full">
                  <Link to="/guest/dashboard">View Dashboard</Link>
                </Button>
              </>
            ) : (
              <>
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl">!</span>
                </div>
                <h1 className="font-serif text-2xl font-bold mb-2">Something went wrong</h1>
                <p className="text-muted-foreground mb-6">
                  We couldn't verify your payment. Please contact support.
                </p>
                <Button asChild className="w-full rounded-full">
                  <Link to="/guest/dashboard">Return to Dashboard</Link>
                </Button>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default PaymentSuccess;
